import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, CircularProgress, MenuItem, Stack, TextField } from "@mui/material";
import dayjs from "dayjs";
import { useIntl } from "react-intl";
import UserContext from "context/UserContext";
import RevaText from "components/common/RevaText";
import performRequest from "network/perform-request";
import {
  leagueCustomerFinancialChargeTemplatesRoute,
  leagueCustomerTournamentsRoute,
  leagueTournamentLineupsRoute,
  leagueTournamentMatchesRoute,
  leagueTournamentSanctionsRoute,
} from "network/api-routes";
import { chargeTypeLabelId } from "constants/external-charge-types";
import { formatFinancialAmount } from "../financial/financial-helpers";
import { leagueTournamentApiId, tournamentMatchesRelationship } from "../gestion-tournament-id";
import { accountApplyChargeRoute } from "./account-finance-routes";
import {
  APPLY_POLICY,
  accountRosterMemberId,
  accountTeamId,
  applicableTemplates,
  applyChargeErrorMessageId,
  applyModeFor,
  buildApplyPayload,
  templateTournamentId,
} from "./apply-template-policy";

const listOf = (data) => (Array.isArray(data) ? data : []);

const tournamentListOf = (data) => {
  const list = data?.results || data?.data || data;
  return Array.isArray(list) ? list : [];
};

const eventTypeOf = (template) => APPLY_POLICY[template?.type]?.eventType ?? null;

const formatDate = (value) => (value ? dayjs(value).format("DD/MM/YYYY") : "—");

// What the final screen says about the API response: a fresh charge, or an existing one the
// apply call reused (paid, waived/voided, or still open).
export const appliedResultKind = (invoice) => {
  if (!invoice?.already_existed) return "created";
  const status = String(invoice.status || "").toLowerCase();
  return status === "paid" ? "alreadyPaid" : status === "void" ? "waived" : "alreadyOpen";
};

const ApplyTemplateWizard = ({ customerId, account, currency, onApplied, onBack }) => {
  const intl = useIntl();
  const { user } = useContext(UserContext);
  const [step, setStep] = useState("template");
  const [appliedInvoice, setAppliedInvoice] = useState(null);
  const [allTemplates, setAllTemplates] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [template, setTemplate] = useState(null);
  const [tournamentId, setTournamentId] = useState("");
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState(false);
  const [eventMessageId, setEventMessageId] = useState(null);
  const [eventId, setEventId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const latestEventsRequest = useRef(0);

  const templates = useMemo(() => applicableTemplates(allTemplates, account), [allTemplates, account]);
  const isEvent = template ? applyModeFor(template, account) === "event" : false;
  const eventType = eventTypeOf(template);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      performRequest("GET", leagueCustomerFinancialChargeTemplatesRoute(customerId), {}, true, user),
      performRequest("GET", leagueCustomerTournamentsRoute(customerId), { page_size: 100 }, true, user),
    ])
      .then(([templatesResponse, tournamentsResponse]) => {
        if (cancelled) return;
        setAllTemplates(listOf(templatesResponse.data));
        setTournaments(tournamentListOf(tournamentsResponse.data));
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId, user]);

  const tournamentNameFor = (relationshipId) =>
    tournaments.find((item) => tournamentMatchesRelationship(item, relationshipId))?.name;

  const templateLabel = (item) => {
    const relationshipId = templateTournamentId(item);
    const scope = relationshipId
      ? (tournamentNameFor(relationshipId) ?? relationshipId)
      : intl.formatMessage({ id: "gestion.financials.globalPriceList" });
    return `${item.name} — ${intl.formatMessage({ id: chargeTypeLabelId(item.type) })} — ${formatFinancialAmount(item.amount, currency)} — ${scope}`;
  };

  const eventLabel = (item) =>
    eventType === "match"
      ? intl.formatMessage(
          { id: "gestion.applyPrice.matchLabel" },
          {
            local: item.local_lineup?.team?.name ?? "—",
            visiting: item.visiting_lineup?.team?.name ?? "—",
            date: formatDate(item.scheduled_time),
          },
        )
      : intl.formatMessage(
          { id: "gestion.applyPrice.sanctionLabel" },
          { date: formatDate(item.sanctioned_at), comments: item.comments || "—" },
        );

  const resetEvents = () => {
    latestEventsRequest.current += 1;
    setTournamentId("");
    setEvents([]);
    setEventId("");
    setEventMessageId(null);
    setEventsError(false);
    setEventsLoading(false);
  };

  const selectTournament = async (value) => {
    const requestId = ++latestEventsRequest.current;
    const isCurrent = () => requestId === latestEventsRequest.current;
    setTournamentId(value);
    setEvents([]);
    setEventId("");
    setEventMessageId(null);
    setEventsError(false);

    // A template can be scoped to a tournament missing from the list (archived, or past the
    // page_size cap); fall back to its id so the event step never dead-ends.
    const tournament =
      tournaments.find((item) => String(leagueTournamentApiId(item)) === String(value)) ??
      (value ? { revaId: value, name: String(value) } : null);
    if (!tournament) return;
    const apiId = leagueTournamentApiId(tournament);

    setEventsLoading(true);
    try {
      if (eventType === "sanction") {
        const response = await performRequest("GET", leagueTournamentSanctionsRoute(apiId), {}, true, user);
        if (!isCurrent()) return;
        const memberId = accountRosterMemberId(account);
        const sanctions = listOf(response.data).filter(
          (sanction) =>
            String(sanction.player?.playerable_id) === memberId &&
            String(sanction.player?.playerable_type || "").endsWith("RosterMember"),
        );
        setEvents(sanctions);
        if (sanctions.length === 0) setEventMessageId("gestion.applyPrice.noSanctions");
        return;
      }

      const lineupsResponse = await performRequest("GET", leagueTournamentLineupsRoute(apiId), {}, true, user);
      if (!isCurrent()) return;
      const lineup = listOf(lineupsResponse.data).find(
        (item) => String(item.team_id ?? item.team?.id) === accountTeamId(account),
      );
      if (!lineup) {
        setEventMessageId("gestion.applyPrice.noLineup");
        return;
      }
      if (eventType === "lineup_register") {
        setEvents([lineup]);
        setEventId(String(lineup.id));
        return;
      }

      const matchesResponse = await performRequest("GET", leagueTournamentMatchesRoute(apiId), {}, true, user);
      if (!isCurrent()) return;
      const matches = listOf(matchesResponse.data).filter((item) =>
        [item.local_lineup_id, item.visiting_lineup_id].some((id) => String(id) === String(lineup.id)),
      );
      setEvents(matches);
      if (matches.length === 0) setEventMessageId("gestion.applyPrice.noMatches");
    } catch {
      if (isCurrent()) setEventsError(true);
    } finally {
      if (isCurrent()) setEventsLoading(false);
    }
  };

  const handleTemplateContinue = () => {
    if (applyModeFor(template, account) !== "event") {
      setStep("confirm");
      return;
    }
    setStep("event");
    const scopedTournamentId = templateTournamentId(template);
    if (scopedTournamentId) selectTournament(scopedTournamentId);
  };

  const applyCharge = async () => {
    setSaving(true);
    setError(null);
    setAppliedInvoice(null);
    try {
      const response = await performRequest(
        "POST",
        accountApplyChargeRoute(customerId, account, template.id),
        buildApplyPayload(template, isEvent ? { id: eventId } : undefined),
        true,
        user,
      );
      setAppliedInvoice(response?.data ?? null);
      setStep("result");
      onApplied?.();
    } catch (requestError) {
      setError(intl.formatMessage({ id: applyChargeErrorMessageId(requestError) }));
    } finally {
      setSaving(false);
    }
  };

  if (step === "result") {
    const kind = appliedResultKind(appliedInvoice);
    return (
      <Stack spacing={2}>
        <RevaText variant="h6">
          {intl.formatMessage({ id: kind === "created" ? "gestion.applyPrice.applied" : "gestion.applyPrice.unchanged" })}
        </RevaText>
        {kind !== "created" && <Alert severity="info">{intl.formatMessage({ id: `gestion.applyPrice.result.${kind}` })}</Alert>}
        <RevaText>{template.name}</RevaText>
        {kind === "created" && (
          <RevaText sx={{ fontWeight: 600 }}>{formatFinancialAmount(template.amount, currency)}</RevaText>
        )}
        <Button variant="contained" onClick={onBack}>
          {intl.formatMessage({ id: "gestion.playerFinance.returnAccount" })}
        </Button>
      </Stack>
    );
  }

  if (step === "confirm") {
    const tournament = tournaments.find((item) => String(leagueTournamentApiId(item)) === tournamentId);
    const selectedEvent = isEvent && eventType !== "lineup_register" ? events.find((item) => String(item.id) === eventId) : null;
    return (
      <Stack spacing={2}>
        <RevaText variant="h6">{intl.formatMessage({ id: "gestion.applyPrice.confirmTitle" })}</RevaText>
        <RevaText>{intl.formatMessage({ id: chargeTypeLabelId(template.type) })}</RevaText>
        <RevaText>{template.name}</RevaText>
        {isEvent && tournament && <RevaText>{tournament.name}</RevaText>}
        {selectedEvent && <RevaText>{eventLabel(selectedEvent)}</RevaText>}
        <RevaText sx={{ fontWeight: 600 }}>
          {`${intl.formatMessage({ id: "gestion.applyPrice.amountFromPrice" })}: ${formatFinancialAmount(template.amount, currency)}`}
        </RevaText>
        <RevaText>
          {intl.formatMessage({ id: isEvent ? "gestion.applyPrice.eventHint" : "gestion.applyPrice.freeHint" })}
        </RevaText>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack direction="row" spacing={1}>
          <Button variant="contained" data-testid="apply-confirm" disabled={saving} onClick={applyCharge}>
            {intl.formatMessage({ id: "gestion.applyPrice.apply" })}
          </Button>
          <Button
            disabled={saving}
            onClick={() => {
              setError(null);
              setStep(isEvent ? "event" : "template");
            }}
          >
            {intl.formatMessage({ id: "gestion.playerFinance.back" })}
          </Button>
        </Stack>
      </Stack>
    );
  }

  if (step === "event") {
    const scopedTournamentId = templateTournamentId(template);
    const scopedOptions = scopedTournamentId
      ? tournaments.filter((item) => tournamentMatchesRelationship(item, scopedTournamentId))
      : [];
    const tournamentOptions = !scopedTournamentId
      ? tournaments
      : scopedOptions.length
        ? scopedOptions
        : [{ revaId: scopedTournamentId, name: String(scopedTournamentId) }];
    const hasEventSelect = eventType !== "lineup_register";
    const canContinue = Boolean(eventId) && !eventsLoading;
    return (
      <Stack spacing={2}>
        <RevaText variant="h6">{intl.formatMessage({ id: "gestion.applyPrice.title" })}</RevaText>
        <TextField
          select
          fullWidth
          label={intl.formatMessage({ id: "gestion.applyPrice.tournament" })}
          value={tournamentId}
          disabled={Boolean(scopedTournamentId)}
          onChange={(event) => selectTournament(event.target.value)}
          SelectProps={{ displayEmpty: true }}
          data-testid="apply-tournament"
        >
          <MenuItem value="" disabled>
            {intl.formatMessage({ id: "gestion.applyPrice.selectTournament" })}
          </MenuItem>
          {tournamentOptions.map((item) => (
            <MenuItem key={String(leagueTournamentApiId(item))} value={String(leagueTournamentApiId(item))}>
              {item.name}
            </MenuItem>
          ))}
        </TextField>
        {eventsLoading && <CircularProgress size={24} />}
        {eventsError && <Alert severity="error">{intl.formatMessage({ id: "gestion.applyPrice.loadError" })}</Alert>}
        {eventMessageId && !eventsLoading && (
          <Alert severity="warning">{intl.formatMessage({ id: eventMessageId })}</Alert>
        )}
        {hasEventSelect && tournamentId && events.length > 0 && !eventsLoading && (
          <TextField
            select
            fullWidth
            label={intl.formatMessage({ id: eventType === "match" ? "gestion.applyPrice.match" : "gestion.applyPrice.sanction" })}
            value={eventId}
            onChange={(event) => setEventId(event.target.value)}
            SelectProps={{ displayEmpty: true }}
            data-testid="apply-event"
          >
            <MenuItem value="" disabled>
              {intl.formatMessage({
                id: eventType === "match" ? "gestion.applyPrice.selectMatch" : "gestion.applyPrice.selectSanction",
              })}
            </MenuItem>
            {events.map((item) => (
              <MenuItem key={item.id} value={String(item.id)}>
                {eventLabel(item)}
              </MenuItem>
            ))}
          </TextField>
        )}
        <Stack direction="row" spacing={1}>
          <Button variant="contained" disabled={!canContinue} onClick={() => setStep("confirm")}>
            {intl.formatMessage({ id: "gestion.playerFinance.continue" })}
          </Button>
          <Button
            onClick={() => {
              resetEvents();
              setStep("template");
            }}
          >
            {intl.formatMessage({ id: "gestion.playerFinance.back" })}
          </Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <RevaText variant="h6">{intl.formatMessage({ id: "gestion.applyPrice.title" })}</RevaText>
      {loading && <CircularProgress size={24} />}
      {!loading && loadError && (
        <Alert severity="error">{intl.formatMessage({ id: "gestion.applyPrice.loadError" })}</Alert>
      )}
      {!loading && !loadError && templates.length === 0 && (
        <Alert severity="info">{intl.formatMessage({ id: "gestion.applyPrice.noTemplates" })}</Alert>
      )}
      {!loading && !loadError && templates.length > 0 && (
        <TextField
          select
          fullWidth
          label={intl.formatMessage({ id: "gestion.applyPrice.selectTemplate" })}
          value={template ? String(template.id) : ""}
          onChange={(event) =>
            setTemplate(templates.find((item) => String(item.id) === event.target.value) ?? null)
          }
          SelectProps={{ displayEmpty: true }}
          data-testid="apply-template-select"
        >
          {templates.map((item) => (
            <MenuItem key={String(item.id)} value={String(item.id)}>
              {templateLabel(item)}
            </MenuItem>
          ))}
        </TextField>
      )}
      <Stack direction="row" spacing={1}>
        <Button variant="contained" disabled={!template} onClick={handleTemplateContinue}>
          {intl.formatMessage({ id: "gestion.playerFinance.continue" })}
        </Button>
        <Button onClick={onBack}>{intl.formatMessage({ id: "gestion.playerFinance.back" })}</Button>
      </Stack>
    </Stack>
  );
};

export default ApplyTemplateWizard;
